/**
 * Synthetic-fixture benchmark rows — mirrored verbatim (every field) from
 * `packages/cli/src/eval/published-benchmark.json` (see `benchmark-data.ts`
 * for the sync chain). Import from `benchmark-data.ts`, not from here.
 */

export interface SyntheticRow {
  readonly id: string;
  readonly fixtures: number;
  readonly f1: number;
  readonly threshold: number;
  /** Auth-detection accuracy across the framework's fixtures. */
  readonly authAccuracy: number;
  /** Structural schema-extraction F1 over the schema-scored routes. */
  readonly schemaF1: number;
  /** How many routes the schema score covers (0 = framework has none scored). */
  readonly schemaScoredRoutes: number;
}

// prettier-ignore
export const SYNTHETIC_ROWS: readonly SyntheticRow[] = [
  { id: 'nestjs', fixtures: 5, f1: 1, threshold: 0.92, authAccuracy: 1, schemaF1: 1, schemaScoredRoutes: 0 },
  { id: 'express', fixtures: 3, f1: 1, threshold: 0.85, authAccuracy: 1, schemaF1: 1, schemaScoredRoutes: 0 },
  { id: 'fastapi', fixtures: 8, f1: 1, threshold: 0.92, authAccuracy: 1, schemaF1: 1, schemaScoredRoutes: 0 },
  { id: 'fastify', fixtures: 3, f1: 1, threshold: 0.85, authAccuracy: 1, schemaF1: 1, schemaScoredRoutes: 1 },
  { id: 'nextjs', fixtures: 8, f1: 1, threshold: 0.92, authAccuracy: 1, schemaF1: 1, schemaScoredRoutes: 0 },
  { id: 'graphql', fixtures: 5, f1: 1, threshold: 1, authAccuracy: 1, schemaF1: 1, schemaScoredRoutes: 9 },
  { id: 'grpc', fixtures: 1, f1: 1, threshold: 0.92, authAccuracy: 1, schemaF1: 1, schemaScoredRoutes: 0 },
  { id: 'trpc', fixtures: 3, f1: 1, threshold: 0.92, authAccuracy: 1, schemaF1: 1, schemaScoredRoutes: 4 },
  { id: 'websocket', fixtures: 2, f1: 1, threshold: 0.85, authAccuracy: 1, schemaF1: 1, schemaScoredRoutes: 0 },
  { id: 'springboot', fixtures: 2, f1: 1, threshold: 0.85, authAccuracy: 1, schemaF1: 1, schemaScoredRoutes: 1 },
  { id: 'django', fixtures: 2, f1: 1, threshold: 0.85, authAccuracy: 1, schemaF1: 1, schemaScoredRoutes: 1 },
  { id: 'flask', fixtures: 2, f1: 1, threshold: 0.85, authAccuracy: 1, schemaF1: 1, schemaScoredRoutes: 0 },
  { id: 'rails', fixtures: 3, f1: 0.9259259259259259, threshold: 0.85, authAccuracy: 1, schemaF1: 1, schemaScoredRoutes: 2 },
  { id: 'phoenix', fixtures: 1, f1: 1, threshold: 0.85, authAccuracy: 1, schemaF1: 1, schemaScoredRoutes: 0 },
  { id: 'gin', fixtures: 1, f1: 1, threshold: 0.85, authAccuracy: 1, schemaF1: 1, schemaScoredRoutes: 0 },
  { id: 'chi', fixtures: 1, f1: 1, threshold: 0.85, authAccuracy: 1, schemaF1: 1, schemaScoredRoutes: 0 },
  { id: 'echo', fixtures: 1, f1: 1, threshold: 0.85, authAccuracy: 1, schemaF1: 1, schemaScoredRoutes: 0 },
  { id: 'fiber', fixtures: 1, f1: 1, threshold: 0.85, authAccuracy: 1, schemaF1: 1, schemaScoredRoutes: 0 },
  { id: 'net-http', fixtures: 1, f1: 1, threshold: 0.85, authAccuracy: 1, schemaF1: 1, schemaScoredRoutes: 0 },
  { id: 'actix', fixtures: 2, f1: 1, threshold: 0.85, authAccuracy: 1, schemaF1: 1, schemaScoredRoutes: 0 },
  { id: 'axum', fixtures: 3, f1: 1, threshold: 0.85, authAccuracy: 1, schemaF1: 1, schemaScoredRoutes: 0 },
  { id: 'laravel', fixtures: 2, f1: 1, threshold: 0.85, authAccuracy: 1, schemaF1: 1, schemaScoredRoutes: 1 },
  { id: 'aspnet', fixtures: 1, f1: 1, threshold: 0.85, authAccuracy: 1, schemaF1: 1, schemaScoredRoutes: 0 },
  { id: 'koa', fixtures: 1, f1: 1, threshold: 0.85, authAccuracy: 1, schemaF1: 1, schemaScoredRoutes: 0 },
  { id: 'hapi', fixtures: 1, f1: 1, threshold: 0.85, authAccuracy: 1, schemaF1: 1, schemaScoredRoutes: 0 },
  { id: 'elysia', fixtures: 1, f1: 1, threshold: 0.85, authAccuracy: 1, schemaF1: 1, schemaScoredRoutes: 0 },
  { id: 'litestar', fixtures: 1, f1: 1, threshold: 0.85, authAccuracy: 1, schemaF1: 1, schemaScoredRoutes: 0 },
  { id: 'sinatra', fixtures: 1, f1: 1, threshold: 0.85, authAccuracy: 1, schemaF1: 1, schemaScoredRoutes: 0 },
  { id: 'ktor', fixtures: 1, f1: 1, threshold: 0.85, authAccuracy: 1, schemaF1: 1, schemaScoredRoutes: 0 },
  { id: 'vapor', fixtures: 1, f1: 1, threshold: 0.75, authAccuracy: 1, schemaF1: 1, schemaScoredRoutes: 0 },
  { id: 'play', fixtures: 1, f1: 1, threshold: 0.75, authAccuracy: 1, schemaF1: 1, schemaScoredRoutes: 0 },
  { id: 'compojure', fixtures: 1, f1: 1, threshold: 0.75, authAccuracy: 1, schemaF1: 1, schemaScoredRoutes: 0 },
  { id: 'dream', fixtures: 2, f1: 1, threshold: 0.75, authAccuracy: 1, schemaF1: 1, schemaScoredRoutes: 0 },
  { id: 'servant', fixtures: 1, f1: 1, threshold: 0.75, authAccuracy: 1, schemaF1: 1, schemaScoredRoutes: 0 },
  { id: 'cowboy', fixtures: 2, f1: 1, threshold: 0.75, authAccuracy: 1, schemaF1: 1, schemaScoredRoutes: 0 },
  { id: 'plumber', fixtures: 1, f1: 1, threshold: 0.75, authAccuracy: 1, schemaF1: 1, schemaScoredRoutes: 0 },
  { id: 'lapis', fixtures: 2, f1: 1, threshold: 0.75, authAccuracy: 1, schemaF1: 1, schemaScoredRoutes: 0 },
] as const;
